<template>
  <div class="dashboard-editor-container">
    <div class="search-container">
      <el-input
        v-model="search"
        size="mini"
        :placeholder="$t('ideas').searchPlaceholder"
        style="width: 100%; max-width: 400px"
      />
    </div>
    <div class="card-container">
      <el-card v-for="(item, index) in validCards" :key="index" class="card">
        <div class="card-header">
          <div class="tag-container">
            <div class="concept-container">
              <span class="concept-item concept1">{{ item.concept1 }}</span>
              <span class="concept-item concept2">{{ item.concept2 }}</span>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div
            class="card-text"
            v-if="
              item.knowledge &&
              typeof item.knowledge === 'string' &&
              item.knowledge.trim()
            "
          >
            <div class="knowledge-content">
              <div
                v-html="
                  highlightConcepts(
                    item.knowledge,
                    item.concept1,
                    item.concept2
                  )
                "
              ></div>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="footer-info">
            <div class="reaction-container">
              <span class="like-count">{{ item.like }} 👍🏻</span>
              <span class="dislike-count">{{ item.dislike }} 👎🏻</span>
            </div>
            <div class="button-container">
              <el-button
                size="mini"
                type="primary"
                plain
                @click="goToReportSearch(item.keyword1)"
                class="action-button report-button"
              >
                {{ $t("ideas").readReport }}
              </el-button>
              <el-button
                size="mini"
                type="success"
                plain
                @click="openDialog(item)"
                class="action-button detail-button"
              >
                {{ $t("ideas").details }}
              </el-button>
              <i
                v-clipboard:copy="item.knowledge"
                class="iconfont el-icon-document-copy copy-button"
                @click="copyResponse"
                :title="$t('ideas').copy"
              ></i>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- Dialog for displaying idea details -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="70%"
      :before-close="handleClose"
      custom-class="idea-dialog"
    >
      <div class="dialog-content" v-if="currentIdea">
        <div class="dialog-section">
          <h3 class="section-title">{{ $t("ideas").basicInformation }}</h3>
          <div class="info-grid">
            <div class="info-item keyword-item">
              <span class="info-label">{{ $t("ideas").keyword }}:</span>
              <span class="info-value keyword-value">{{
                truncateKeyword(currentIdea.keyword1)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("ideas").concept1 }}:</span>
              <span class="info-value concept-badge concept1">{{
                currentIdea.concept1
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("ideas").concept2 }}:</span>
              <span class="info-value concept-badge concept2">{{
                currentIdea.concept2
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("ideas").reactions }}:</span>
              <span class="info-value reaction-value">
                <span class="like-count">{{ currentIdea.like }} 👍🏻</span>
                <span class="dislike-count">{{ currentIdea.dislike }} 👎🏻</span>
              </span>
            </div>
          </div>
        </div>

        <div class="dialog-section">
          <h3 class="section-title">{{ $t("ideas").knowledgeContent }}</h3>
          <div class="knowledge-dialog-content">
            <div
              v-html="
                highlightConcepts(
                  currentIdea.knowledge,
                  currentIdea.concept1,
                  currentIdea.concept2
                )
              "
            ></div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose">{{ $t("ideas").close }}</el-button>
        <el-button type="success" @click="copyDialogContent">{{
          $t("ideas").copyContent
        }}</el-button>
        <el-button
          type="primary"
          @click="goToReportSearch(currentIdea.keyword1)"
          >{{ $t("ideas").viewReport }}</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getIdeas } from "@/api/api";
export default {
  name: "ideas",
  data() {
    return {
      tableData: [],
      search: "",
      dialogVisible: false,
      currentIdea: null,
    };
  },
  methods: {
    // 将+符号替换为空格
    replacePlusWithSpace(str) {
      if (!str) return "";
      return String(str).replace(/\+/g, " ");
    },

    // 高亮知识内容中的概念词
    highlightConcepts(knowledge, concept1, concept2) {
      if (!knowledge) return "";

      let highlightedText = String(knowledge);

      // 收集所有需要高亮的概念词
      const concepts = [];
      if (concept1 && concept1 !== "-") {
        concepts.push(concept1);
      }
      if (concept2 && concept2 !== "-") {
        concepts.push(concept2);
      }

      // 去重并过滤空值
      const uniqueConcepts = [...new Set(concepts)].filter(
        (concept) => concept && concept.trim()
      );

      // 对每个概念词进行高亮
      uniqueConcepts.forEach((concept) => {
        if (concept && concept.trim()) {
          // 创建不区分大小写的正则表达式，添加转义
          const escapedConcept = concept
            .trim()
            .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(`(${escapedConcept})`, "gi");
          highlightedText = highlightedText.replace(
            regex,
            '<mark class="highlighted-concept">$1</mark>'
          );
        }
      });

      return highlightedText;
    },

    // 解析 knowledge 字段，提取 JSON 中的 knowledge 值
    parseKnowledge(knowledgeStr) {
      if (!knowledgeStr) return "";

      try {
        // 如果已经是对象，直接处理
        if (typeof knowledgeStr === "object") {
          if (knowledgeStr.knowledge) {
            return this.replacePlusWithSpace(knowledgeStr.knowledge);
          }
          // 如果没有 knowledge 字段，尝试其他可能的字段
          return this.replacePlusWithSpace(
            knowledgeStr.content || knowledgeStr.text || ""
          );
        }

        // 如果是字符串，尝试解析 JSON
        if (typeof knowledgeStr === "string") {
          // 尝试解析 JSON
          const parsed = JSON.parse(knowledgeStr);
          if (parsed && typeof parsed === "object") {
            // 提取 knowledge 字段
            if (parsed.knowledge) {
              return this.replacePlusWithSpace(parsed.knowledge);
            }
            // 如果没有 knowledge 字段，尝试其他可能的字段
            return this.replacePlusWithSpace(
              parsed.content || parsed.text || ""
            );
          }
          // 如果解析失败但仍是字符串，直接返回并处理+符号
          return this.replacePlusWithSpace(knowledgeStr);
        }

        // 其他类型转换为字符串并处理+符号
        return this.replacePlusWithSpace(knowledgeStr);
      } catch (e) {
        // JSON 解析失败，如果是对象，尝试直接提取字段
        if (typeof knowledgeStr === "object") {
          return this.replacePlusWithSpace(
            knowledgeStr.knowledge ||
              knowledgeStr.content ||
              knowledgeStr.text ||
              ""
          );
        }
        // 如果不是对象，直接返回字符串并处理+符号
        return this.replacePlusWithSpace(knowledgeStr);
      }
    },

    getTableData() {
      this.tableData = [];
      getIdeas()
        .then((res) => {
          const data = res.data.data;

          // 根据后端逻辑处理数据
          for (let userId in data) {
            const userIdeas = data[userId];
            for (let idea of userIdeas) {
              // 解析 knowledge 字段
              const rawKnowledge = idea.idea || idea.knowledge || "";
              const parsedKnowledge = this.parseKnowledge(rawKnowledge);

              this.tableData.push({
                user_id: idea.user_id,
                user_name: idea.user_name || "",
                keyword1: this.replacePlusWithSpace(idea.keyword1) || "-",
                keyword2: this.replacePlusWithSpace(idea.keyword2) || "", // keyword2 应该是空字符串
                concept1: this.replacePlusWithSpace(idea.concept1) || "-",
                concept2: this.replacePlusWithSpace(idea.concept2) || "-",
                like: idea.like || 0,
                dislike: idea.dislike || 0,
                knowledge: parsedKnowledge, // 使用解析后的 knowledge
              });
            }
          }
        })
        .catch((e) => {
          console.log("[ERROR IN getIdeas]", e);
        });
    },

    copyResponse() {
      this.$notify({
        title: this.$t("ideas").notify.success,
        message: this.$t("ideas").notify.contentCopied,
        type: "success",
      });
    },

    goToReportSearch(keyword) {
      // 跳转到 reportSearch 页面并传递参数，新页面打开
      // 先处理+符号替换为空格，然后编码
      const processedKeyword = this.replacePlusWithSpace(keyword);
      const encodedKeyword = encodeURIComponent(processedKeyword);
      const url = `/#/reportSearch?q=${encodedKeyword}`;
      window.open(url, "_blank");
    },

    // 打开对话框显示详情
    openDialog(idea) {
      this.currentIdea = idea;
      this.dialogVisible = true;
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false;
      this.currentIdea = null;
    },

    // 截断keyword，最多显示50个字符
    truncateKeyword(keyword) {
      if (!keyword) return "";
      if (keyword.length <= 50) return keyword;
      return keyword.substring(0, 50) + "...";
    },

    // 复制对话框内容
    copyDialogContent() {
      if (!this.currentIdea) return;

      const ideas = this.$t("ideas");
      const content = `${ideas.copyFormat.keyword}: ${this.currentIdea.keyword1}
${ideas.copyFormat.concept1}: ${this.currentIdea.concept1}
${ideas.copyFormat.concept2}: ${this.currentIdea.concept2}
${ideas.copyFormat.likes}: ${this.currentIdea.like}
${ideas.copyFormat.dislikes}: ${this.currentIdea.dislike}

${ideas.copyFormat.knowledgeContent}:
${this.currentIdea.knowledge}`;

      navigator.clipboard
        .writeText(content)
        .then(() => {
          this.$notify({
            title: ideas.notify.success,
            message: ideas.notify.contentCopiedToClipboard,
            type: "success",
          });
        })
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = content;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);

          this.$notify({
            title: ideas.notify.success,
            message: ideas.notify.contentCopiedToClipboard,
            type: "success",
          });
        });
    },
  },
  computed: {
    filteredData() {
      return this.tableData.filter(
        (data) =>
          !this.search ||
          data.knowledge.toLowerCase().includes(this.search.toLowerCase()) ||
          data.keyword1.toLowerCase().includes(this.search.toLowerCase()) ||
          data.concept1.toLowerCase().includes(this.search.toLowerCase()) ||
          data.concept2.toLowerCase().includes(this.search.toLowerCase()) ||
          (data.user_name &&
            data.user_name.toLowerCase().includes(this.search.toLowerCase()))
      );
    },

    // 只显示有知识内容的卡片
    validCards() {
      return this.filteredData.filter(
        (item) =>
          item.knowledge &&
          typeof item.knowledge === "string" &&
          item.knowledge.trim()
      );
    },

    // 对话框标题
    dialogTitle() {
      if (!this.currentIdea) return this.$t("ideas").dialogTitle;
      const template = this.$t("ideas").dialogTitleWithConcepts;
      return template
        .replace("{concept1}", this.currentIdea.concept1)
        .replace("{concept2}", this.currentIdea.concept2);
    },
  },
  mounted() {
    this.getTableData();
  },
};
</script>

<style scoped>
.dashboard-editor-container {
  padding: 20px;
}

.search-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.card {
  width: 300px;
  border-radius: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.card:hover {
  transform: translateY(-5px);
}

.card-header {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.tag-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
}

.concept-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.concept-item {
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.concept-item.concept1 {
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
  color: #2d5016;
  border-color: #a8d5a8;
}

.concept-item.concept2 {
  background: linear-gradient(135deg, #e8f4fd 0%, #f0f6fd 100%);
  color: #1a4d8f;
  border-color: #b8d4f7;
}

.concept-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Dialog Styles */
.idea-dialog .el-dialog__body {
  padding: 20px;
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  border-left: 4px solid #409eff;
  padding-left: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.info-label {
  font-weight: 500;
  color: #606266;
  margin-right: 8px;
  min-width: 80px;
}

.info-value {
  color: #303133;
  font-weight: 500;
}

/* Keyword column takes double width */
.keyword-item {
  grid-column: span 2;
}

/* Keyword truncation styling */
.keyword-value {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.concept-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.concept-badge.concept1 {
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
  color: #2d5016;
  border: 1px solid #a8d5a8;
}

.concept-badge.concept2 {
  background: linear-gradient(135deg, #e8f4fd 0%, #f0f6fd 100%);
  color: #1a4d8f;
  border: 1px solid #b8d4f7;
}

.knowledge-dialog-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  line-height: 1.6;
  font-size: 14px;
  max-height: 300px;
  overflow-y: auto;
}

.knowledge-dialog-content .highlighted-concept {
  background-color: #fff3cd;
  color: #856404;
  padding: 1px 2px;
  border-radius: 3px;
  font-weight: 600;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.card-body {
  padding: 10px;
}

.card-text {
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
  position: relative;
}

.knowledge-content {
  max-height: 250px;
  overflow-y: auto;
  padding-right: 20px;
  word-wrap: break-word;
}

.highlighted-concept {
  background-color: #fff3cd;
  color: #856404;
  padding: 1px 2px;
  border-radius: 3px;
  font-weight: 600;
}

.copy-button {
  cursor: pointer;
  color: rgb(45, 146, 255);
  transition: all 0.3s;
  font-size: 16px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: 8px;
}

.copy-button:hover {
  background-color: rgba(64, 158, 255, 0.1);
  transform: translateY(-1px);
}

.empty-knowledge {
  text-align: center;
  color: #909399;
  font-style: italic;
}

.empty-text {
  font-size: 12px;
}

.card-footer {
  padding: 8px 12px;
  border-top: 1px solid #e0e0e0;
}

.footer-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}

.button-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-button {
  padding: 2px 6px;
  height: 20px;
  font-size: 10px;
  line-height: 1;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 60px;
  border: none;
  font-weight: 500;
}

.action-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.report-button {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: white;
}

.report-button:hover {
  background: linear-gradient(135deg, #66b1ff 0%, #409eff 100%);
}

.detail-button {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  color: white;
}

.detail-button:hover {
  background: linear-gradient(135deg, #85ce61 0%, #67c23a 100%);
}

.copy-button {
  cursor: pointer;
  color: rgb(45, 146, 255);
  transition: all 0.3s;
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 3px;
  margin-left: 4px;
}

.copy-button:hover {
  background-color: rgba(64, 158, 255, 0.1);
  transform: translateY(-1px);
}

.user-info {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.reaction-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.like-count {
  font-size: 13px;
  padding: 2px 6px;
  color: #67c23a;
  background-color: rgba(103, 194, 58, 0.1);
  border-radius: 12px;
  font-weight: 500;
}

.dislike-count {
  font-size: 13px;
  padding: 2px 6px;
  color: #f56c6c;
  background-color: rgba(245, 108, 108, 0.1);
  border-radius: 12px;
  font-weight: 500;
}

/* 暗黑模式适配 */
.dark-mode .dashboard-editor-container {
  background-color: #1e1e1e !important;
}

.dark-mode .card-container .card {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3) !important;
}

.dark-mode .card-body .card-text {
  color: #e0e0e0 !important;
}

.dark-mode .card-header,
.dark-mode .card-footer {
  border-color: #444 !important;
}

.dark-mode .like-count {
  color: #90caf9 !important;
  background-color: rgba(144, 202, 249, 0.15) !important;
}

.dark-mode .dislike-count {
  color: #f78989 !important;
  background-color: rgba(247, 137, 137, 0.15) !important;
}

/* 对话框夜间模式 */
.dark-mode .idea-dialog .el-dialog {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
}

.dark-mode .idea-dialog .el-dialog__header {
  border-bottom-color: #444 !important;
}

.dark-mode .idea-dialog .el-dialog__title {
  color: #e0e0e0 !important;
}

.dark-mode .idea-dialog .el-dialog__body {
  color: #e0e0e0 !important;
}

.dark-mode .section-title {
  color: #e0e0e0 !important;
  border-left-color: #66b1ff !important;
}

.dark-mode .info-item {
  background-color: #3d3d3d !important;
}

.dark-mode .keyword-item {
  background-color: #3d3d3d !important;
}

.dark-mode .dialog-section {
  background-color: transparent !important;
}

.dark-mode .info-label {
  color: #b0b0b0 !important;
}

.dark-mode .info-value {
  color: #e0e0e0 !important;
}

.dark-mode .keyword-value {
  color: #e0e0e0 !important;
}

.dark-mode .concept-badge.concept1 {
  background: linear-gradient(135deg, #2d4d2d 0%, #3a5c3a 100%) !important;
  color: #a8d5a8 !important;
  border-color: #3a5c3a !important;
}

.dark-mode .concept-badge.concept2 {
  background: linear-gradient(135deg, #2d3d5c 0%, #3a4d6e 100%) !important;
  color: #b8d4f7 !important;
  border-color: #3a4d6e !important;
}

.dark-mode .knowledge-dialog-content {
  background-color: #3d3d3d !important;
  border-color: #444 !important;
  color: #e0e0e0 !important;
}

.dark-mode .knowledge-dialog-content .highlighted-concept {
  background-color: #5d4c1a !important;
  color: #ffd700 !important;
}

.dark-mode .el-button {
  background-color: #3d3d3d !important;
  border-color: #444 !important;
  color: #e0e0e0 !important;
}

.dark-mode .el-button:hover {
  background-color: #4d4d4d !important;
  border-color: #555 !important;
}

.dark-mode .el-button--primary {
  background-color: #409eff !important;
  border-color: #409eff !important;
  color: white !important;
}

.dark-mode .el-button--success {
  background-color: #67c23a !important;
  border-color: #67c23a !important;
  color: white !important;
}
</style>
